import {
    Paper,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography,
    Box,
    Tooltip,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import React, { useEffect, useMemo, useState } from 'react';
import { immediateToast } from 'utils/toast';
import AnimatedLayout, {
    AnimatedLayoutVariant,
} from '../../../components/layouts/animated-layout';
import { useWalletsQuery } from 'app/store';
import { addCommas } from '@persian-tools/persian-tools';
import { CardWithHeader } from '../../../components/layouts/side-tab-layout/sub-header';

type NotificationSettingProps = {
    notifications: Array<{
        name: string;
        label: string;
        services?: string[];
    }>;
    values?: Record<string, Record<string, boolean>>;
    onChange?: (notificationName: string, serviceName: string) => void;
    url?: string;
    onResponse?: (response: unknown, dataResponse: any) => void;
    noCard?: boolean;
    animationVariant?: AnimatedLayoutVariant;
};

const services: Record<string, string> = {
    email: 'ایمیل',
    sms: 'پیامک',
    telegram: 'تلگرام',
    notification: 'نوتیفیکیشن',
};

const serviceIcons: Record<string, string> = {
    email: 'fa-light fa-envelope',
    sms: 'fa-light fa-message-lines',
    telegram: 'fa-brands fa-telegram',
    notification: 'fa-light fa-bell',
};

const token = localStorage.getItem('token');

const NotificationSetting: React.FC<NotificationSettingProps> = ({
    notifications,
    values: defaultValues,
    onChange,
    url,
    onResponse,
    noCard = false,
    animationVariant = 'default',
}): JSX.Element => {
    const [values, updateValues] = useState<
        Record<string, Record<string, boolean>>
    >({});
    const [isLoading, updateIsLoading] = useState<boolean>(false);
    const { data: walletList } = useWalletsQuery({});

    const smsDescription = useMemo(() => {
        const smsPrice = walletList?.find((item) => item?.name === 'sms')?.price;

        return `هزینه ارسال هر پیامک ${addCommas(
            String(smsPrice ?? 0),
        )} ریال است و از اعتبار پیامک کسر می‌شود.`;
    }, [walletList]);

    useEffect(() => {
        if (defaultValues) {
            updateValues(defaultValues);
        }
    }, [defaultValues]);

    useEffect(() => {
        if (!defaultValues && url) {
            updateIsLoading(true);
            fetch(url, {
                method: 'GET',
                headers: {
                    Accept: 'application/json',
                    Authorization: 'Bearer ' + token,
                },
            })
                .then((result: any) => result?.json())
                .then((result: any) => {
                    updateValues(result?.data ?? {});
                })
                .finally(() => {
                    updateIsLoading(false);
                });
        }
    }, [defaultValues, url]);

    const handleOnChange = (notificationName: string, serviceName: string) => {
        onChange && onChange(notificationName, serviceName);

        const currentValue = Boolean(
            values?.[notificationName]?.[serviceName],
        );
        const nextValue = !currentValue;

        updateIsLoading(true);
        updateValues((currentValues) => ({
            ...currentValues,
            [notificationName]: {
                ...(currentValues?.[notificationName] ?? {}),
                [serviceName]: nextValue,
            },
        }));

        const dataResponse = {
            name: notificationName,
            service: services[serviceName],
            status: nextValue,
        };

        if (!url) {
            updateIsLoading(false);
            return;
        }

        fetch(url, {
            method: 'POST',
            headers: {
                Accept: 'application/json',
                'Content-Type': 'application/json',
                Authorization: 'Bearer ' + token,
            },
            body: JSON.stringify({
                feature: notificationName,
                channel: serviceName,
                is_active: nextValue,
            }),
        })
            .then((res) => {
                if (!res.ok) throw new Error('request failed');
                return res.json();
            })
            .then((data) => {
                const savedValues = data?.data;
                if (savedValues) {
                    updateValues(savedValues);
                }
                onResponse && onResponse(savedValues, dataResponse);
            })
            .catch(() => {
                immediateToast('error', {
                    message: 'خطای غیر منتظره رخ داده است',
                    timeout: 3000,
                });
                updateValues((currentValues) => ({
                    ...currentValues,
                    [notificationName]: {
                        ...(currentValues?.[notificationName] ?? {}),
                        [serviceName]: currentValue,
                    },
                }));
            })
            .finally(() => {
                updateIsLoading(false);
            });
    };

    const switchSx = (theme: any) => ({
        flexShrink: 0,
        margin: 0,
        '.MuiSwitch-track': {
            backgroundColor:
                theme.palette.mode === 'dark'
                    ? theme.palette.background.light
                    : theme.palette.text.disabled,
            opacity: 1,
        },
        '.Mui-checked + .MuiSwitch-track': {
            backgroundColor: theme.palette.primary.main,
            opacity: 1,
        },
        '.MuiSwitch-thumb': {
            backgroundColor:
                theme.palette.mode === 'dark'
                    ? theme.palette.text.secondary
                    : theme.palette.common.white,
        },
        '.Mui-checked .MuiSwitch-thumb': {
            backgroundColor: theme.palette.primary.contrastText,
        },
    });

    const desktopTable = (
        <TableContainer
            component={Paper}
            sx={{
                display: { xs: 'none', sm: 'block' },
                background: 'transparent',
                boxShadow: 0,
            }}
        >
            <Table
                sx={{
                    tableLayout: 'fixed',
                    width: '100%',
                }}
            >
                <TableHead>
                    <TableRow sx={{ backgroundColor: 'background.light' }}>
                        <TableCell
                            sx={{
                                borderColor: 'divider',
                                width: '36%',
                                fontWeight: 600,
                                color: 'text.primary',
                            }}
                        >
                            عملیات
                        </TableCell>

                        {Object.entries(services).map(([key, label]) => (
                            <TableCell
                                key={key}
                                align="center"
                                sx={{
                                    borderColor: 'divider',
                                    width: '16%',
                                    color: 'text.secondary',
                                    fontWeight: 400,
                                }}
                            >
                                <Box
                                    sx={{
                                        display: 'inline-flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        gap: 0.75,
                                    }}
                                >
                                    {key === 'sms' && (
                                        <Tooltip
                                            title={smsDescription}
                                            placement="top"
                                            arrow
                                        >
                                            <i className="fa-light fa-info-circle" />
                                        </Tooltip>
                                    )}
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            textAlign: 'center',
                                            fontWeight: 400,
                                        }}
                                    >
                                        {label}
                                    </Typography>
                                </Box>
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>

                <TableBody>
                    {notifications.map(
                        ({
                            label,
                            name: notificationName,
                            services: allowedServices,
                        }) => (
                            <TableRow
                                key={notificationName}
                                sx={{
                                    '&:last-child td, &:last-child th': {
                                        border: 0,
                                    },
                                }}
                            >
                                <TableCell
                                    component="th"
                                    scope="row"
                                    sx={{
                                        borderColor: 'divider',
                                        fontWeight: 500,
                                        color: 'text.primary',
                                    }}
                                >
                                    {label}
                                </TableCell>

                                {Object.keys(services).map((serviceName) => {
                                    const isSupported =
                                        !allowedServices ||
                                        allowedServices.includes(serviceName);

                                    if (!isSupported) {
                                        return (
                                            <TableCell
                                                key={serviceName}
                                                align="center"
                                                sx={{
                                                    borderColor: 'divider',
                                                    paddingInline: 1,
                                                    color: 'text.disabled',
                                                }}
                                            >
                                                —
                                            </TableCell>
                                        );
                                    }

                                    return (
                                        <TableCell
                                            key={serviceName}
                                            align="center"
                                            sx={{
                                                borderColor: 'divider',
                                                paddingInline: 1,
                                            }}
                                        >
                                            <Switch
                                                disabled={isLoading}
                                                checked={Boolean(
                                                    values?.[notificationName]?.[
                                                        serviceName
                                                    ],
                                                )}
                                                onChange={() =>
                                                    handleOnChange(
                                                        notificationName,
                                                        serviceName,
                                                    )
                                                }
                                                sx={switchSx}
                                            />
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                        ),
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    );

    const mobileCards = (
        <Box
            sx={{
                display: { xs: 'grid', sm: 'none' },
                gap: 1.5,
                px: 2,
                pb: 0.5,
            }}
        >
            <Box
                sx={(theme) => ({
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 1.25,
                    px: 1.5,
                    py: 1.25,
                    borderRadius: 2.5,
                    border: '1px solid',
                    borderColor:
                        theme.palette.mode === 'dark'
                            ? alpha(theme.palette.primary.main, 0.22)
                            : alpha(theme.palette.primary.main, 0.15),
                    backgroundColor:
                        theme.palette.mode === 'dark'
                            ? alpha(theme.palette.primary.main, 0.08)
                            : alpha(theme.palette.primary.main, 0.035),
                })}
            >
                <Box
                    sx={(theme) => ({
                        width: 32,
                        height: 32,
                        minWidth: 32,
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: 2,
                        color:
                            theme.palette.mode === 'dark'
                                ? theme.palette.primary.light
                                : theme.palette.primary.main,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                    })}
                >
                    <i className="fa-light fa-message-lines" />
                </Box>

                <Typography
                    variant="caption"
                    sx={{
                        color: 'text.secondary',
                        lineHeight: 1.9,
                        pt: 0.15,
                    }}
                >
                    {smsDescription}
                </Typography>
            </Box>

            {notifications.map(
                ({ label, name: notificationName, services: allowedServices }) => (
                    <Paper
                        key={notificationName}
                        variant="outlined"
                        sx={(theme) => ({
                            overflow: 'hidden',
                            borderRadius: 3,
                            borderColor:
                                theme.palette.mode === 'dark'
                                    ? alpha(theme.palette.common.white, 0.09)
                                    : theme.palette.divider,
                            backgroundColor:
                                theme.palette.mode === 'dark'
                                    ? theme.palette.background.paper
                                    : theme.palette.common.white,
                            boxShadow:
                                theme.palette.mode === 'dark'
                                    ? `0 8px 24px ${alpha('#000000', 0.12)}`
                                    : `0 6px 20px ${alpha('#172033', 0.055)}`,
                        })}
                    >
                        <Box
                            sx={(theme) => ({
                                display: 'flex',
                                alignItems: 'center',
                                gap: 1.1,
                                px: 1.75,
                                py: 1.4,
                                borderBottom: '1px solid',
                                borderColor: 'divider',
                                backgroundColor:
                                    theme.palette.mode === 'dark'
                                        ? alpha(theme.palette.common.white, 0.018)
                                        : alpha(theme.palette.text.primary, 0.018),
                            })}
                        >
                            <Box
                                sx={(theme) => ({
                                    width: 34,
                                    height: 34,
                                    minWidth: 34,
                                    display: 'grid',
                                    placeItems: 'center',
                                    borderRadius: 2,
                                    fontSize: 14,
                                    color:
                                        theme.palette.mode === 'dark'
                                            ? theme.palette.primary.light
                                            : theme.palette.primary.main,
                                    backgroundColor: alpha(
                                        theme.palette.primary.main,
                                        theme.palette.mode === 'dark' ? 0.13 : 0.07,
                                    ),
                                })}
                            >
                                <i className="fa-light fa-bell-on" />
                            </Box>

                            <Typography
                                variant="body2"
                                className="font-yekan-heavy"
                                sx={{ color: 'text.primary', lineHeight: 1.7 }}
                            >
                                {label}
                            </Typography>
                        </Box>

                        <Box>
                            {Object.keys(services).map((serviceName, index) => {
                                const isSupported =
                                    !allowedServices ||
                                    allowedServices.includes(serviceName);

                                if (!isSupported) {
                                    return null;
                                }

                                return (
                                    <Box
                                        key={serviceName}
                                        sx={{
                                            minHeight: 58,
                                            px: 1.75,
                                            py: 0.7,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            gap: 1.5,
                                            borderTop:
                                                index === 0 ? 0 : '1px solid',
                                            borderColor: 'divider',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                minWidth: 0,
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: 1.2,
                                            }}
                                        >
                                            <Box
                                                sx={(theme) => ({
                                                    width: 32,
                                                    height: 32,
                                                    minWidth: 32,
                                                    display: 'grid',
                                                    placeItems: 'center',
                                                    borderRadius: 2,
                                                    fontSize: 15,
                                                    color: 'text.secondary',
                                                    backgroundColor:
                                                        theme.palette.mode === 'dark'
                                                            ? alpha(
                                                                  theme.palette.common
                                                                      .white,
                                                                  0.035,
                                                              )
                                                            : alpha(
                                                                  theme.palette.text
                                                                      .primary,
                                                                  0.035,
                                                              ),
                                                })}
                                            >
                                                <i
                                                    className={
                                                        serviceIcons[serviceName]
                                                    }
                                                />
                                            </Box>

                                            <Typography
                                                variant="body2"
                                                sx={{
                                                    color: 'text.primary',
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {services[serviceName]}
                                            </Typography>
                                        </Box>

                                        <Switch
                                            disabled={isLoading}
                                            checked={Boolean(
                                                values?.[notificationName]?.[
                                                    serviceName
                                                ],
                                            )}
                                            onChange={() =>
                                                handleOnChange(
                                                    notificationName,
                                                    serviceName,
                                                )
                                            }
                                            sx={switchSx}
                                        />
                                    </Box>
                                );
                            })}
                        </Box>
                    </Paper>
                ),
            )}
        </Box>
    );

    const content = (
        <>
            {mobileCards}
            {desktopTable}
        </>
    );

    return (
        <AnimatedLayout variant={animationVariant}>
            {noCard ? (
                content
            ) : (
                <CardWithHeader title="تنظیمات اطلاع‌رسانی">
                    {content}
                </CardWithHeader>
            )}
        </AnimatedLayout>
    );
};

export default NotificationSetting;
